import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { Icon } from "./icons";
import { Empty, Avatar } from "./ui";
import { Gantt } from "./Gantt";
import { stepState } from "../lib/schedule";
import type { PlaybookRun, PlaybookRunStep, TaskType, Task } from "../lib/types";
import * as work from "../lib/workClient";

/* Shared renderer for a set of plan runs: a Gantt per run, and clicking any
 * step opens a detail modal where you can see its schedule + dependencies,
 * fill out its task's required fields, and mark it complete. Completion routes
 * through both gates — dependencies must be done and required fields filled.
 * Used by the Account 360 and Relationship 360 Plan tabs. */

export function PlanRuns({ runs, mode, onChanged }: { runs: PlaybookRun[]; mode: work.Mode; onChanged: () => void }) {
  const { ws, me } = useWorkspace();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [flash, setFlash] = useState<string | null>(null);
  const [open, setOpen] = useState<{ run: PlaybookRun; step: PlaybookRunStep } | null>(null);

  useEffect(() => { work.loadTasks(ws).then((r) => setTasks(r.tasks)); }, [ws, runs]);
  const say = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2800); };

  const openTask = open ? tasks.find((t) => t.id === open.step.taskId) : undefined;
  const openType = openTask?.typeId ? ws.taskTypes.find((tt) => tt.id === openTask.typeId) : undefined;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {flash && <div className="badge badge--good">{flash}</div>}
      {runs.length === 0 ? (
        <div className="card"><div className="card__bd"><Empty>No plan running here yet.</Empty></div></div>
      ) : runs.map((run) => {
        const done = run.steps.filter((s) => s.status === "done").length;
        const total = run.steps.filter((s) => s.status !== "skipped").length;
        return (
          <div key={run.id} className="card">
            <div className="card__hd">
              <div>
                <h3>{run.playbookName}</h3>
                <span className="sub">{run.scopeKind === "relationship" ? `Relationship · ${run.scopeName}` : "Account"} · starts {run.planStartDate ?? run.startedAt.slice(0, 10)} · {done}/{total} done</span>
              </div>
              <span className={`badge badge--${run.status === "completed" ? "good" : run.status === "stopped" ? "neutral" : "watch"}`}>{run.status}</span>
            </div>
            <div className="card__bd">
              <Gantt run={run} onOpenStep={(step) => setOpen({ run, step })} />
              <div className="faint" style={{ fontSize: 11.5, marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span>Click any step to open it.</span>
                <span><b style={{ color: "var(--risk)" }}>▬</b> critical path</span>
                <span><b style={{ color: "var(--good)" }}>▬</b> done</span>
                <span><b style={{ color: "var(--corona)" }}>▬</b> eligible</span>
                <span><Icon name="lock" size={11} /> blocked</span>
                <span><b style={{ color: "var(--line)" }}>▬</b> slack</span>
              </div>
            </div>
          </div>
        );
      })}

      {open && <StepDetailModal
        run={open.run} step={open.step} task={openTask} type={openType}
        onClose={() => setOpen(null)}
        onComplete={async (fields) => {
          if (open.step.taskId && Object.keys(fields).length) await work.saveTaskFields(mode, ws, openTask ?? { id: open.step.taskId, fields: {} } as Task, fields);
          const res = await work.completeRunStep(mode, ws, open.run.id, open.step.id, me.id);
          if (res.ok) { say(`Completed "${open.step.label}"`); setOpen(null); onChanged(); }
          return res;
        }} />}
    </div>
  );
}

function fieldOk(f: TaskType["fields"][number], v: string | number | boolean | undefined): boolean {
  if (v === undefined || v === null) return false;
  if (f.kind === "checkbox") return v === true;
  if (f.kind === "number") return typeof v === "number" && !Number.isNaN(v);
  if (f.kind === "select") return typeof v === "string" && (f.options ?? []).includes(v);
  return String(v).trim().length > 0;
}

const kindIcon: Record<string, string> = { task: "check", meeting: "calendar", email: "mail", alert: "bell", trigger: "bolt", wait: "clock", branch: "route" };

function StepDetailModal({ run, step, task, type, onClose, onComplete }: {
  run: PlaybookRun; step: PlaybookRunStep; task?: Task; type?: TaskType;
  onClose: () => void; onComplete: (fields: Record<string, string | number | boolean>) => Promise<{ ok: boolean }>;
}) {
  const { ws } = useWorkspace();
  const byId = useMemo(() => new Map(run.steps.map((s) => [s.id, s])), [run.steps]);
  const state = stepState(step, byId);
  const deps = step.dependsOn.map((d) => byId.get(d)).filter((s): s is PlaybookRunStep => !!s);
  const unmet = deps.filter((d) => d.status !== "done" && d.status !== "skipped");
  const owner = task ? ws.users.find((u) => u.id === task.ownerId)?.name : undefined;
  const completedBy = task?.completedBy ? ws.users.find((u) => u.id === task.completedBy)?.name : undefined;
  const completedByOther = !!task?.completedBy && task.completedBy !== task?.ownerId; // completer differs from owner

  const [values, setValues] = useState<Record<string, string | number | boolean>>({ ...(task?.fields ?? {}) });
  const [busy, setBusy] = useState(false);
  const missing = (type?.fields ?? []).filter((f) => f.required && !fieldOk(f, values[f.key]));
  const canComplete = state !== "done" && unmet.length === 0 && missing.length === 0;

  const stateBadge = state === "done" ? "good" : state === "eligible" ? "watch" : state === "blocked" ? "risk" : "neutral";

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="compose-modal__hd">
          <div className="row" style={{ gap: 8 }}><Icon name={type?.icon || kindIcon[step.kind] || "check"} size={16} /><b>{step.label}</b></div>
          <button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button>
        </div>
        <div className="compose-modal__bd" style={{ display: "grid", gap: 14, maxHeight: "66vh", overflowY: "auto" }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span className={`badge badge--${stateBadge}`}>{state}</span>
            {step.critical && step.status !== "done" && <span className="tag" style={{ color: "var(--risk)", borderColor: "rgba(187,71,44,.3)" }}>critical path</span>}
            {type && <span className="tag" style={{ color: "var(--corona-ink)" }}><Icon name="lock" size={10} /> {type.name}</span>}
          </div>

          {/* schedule */}
          <div className="kv">
            <dt>Scheduled</dt><dd>{step.startDate} → {step.dueDate}</dd>
            <dt>Duration</dt><dd>{step.durationDays} day{step.durationDays === 1 ? "" : "s"}</dd>
            <dt>Slack</dt><dd>{step.slack === 0 ? "0 (on the critical path)" : `${step.slack} day${step.slack === 1 ? "" : "s"}`}</dd>
            {owner && <><dt>Owner</dt><dd><span className="row" style={{ gap: 6 }}><Avatar name={owner} size="sm" /> {owner}</span></dd></>}
            {completedBy && <><dt>Completed by</dt><dd><span className="row" style={{ gap: 6 }}><Avatar name={completedBy} size="sm" /> {completedBy}{completedByOther && owner && <span className="faint">· assigned to {owner}</span>}</span></dd></>}
            {task && <><dt>Account</dt><dd><Link to={`/accounts/${task.accountId}`}>{task.accountName}</Link></dd></>}
          </div>

          {/* dependencies */}
          {deps.length > 0 && (
            <div>
              <div className="labeled__l" style={{ marginBottom: 6 }}>Depends on</div>
              <div style={{ display: "grid", gap: 5 }}>
                {deps.map((d) => (
                  <div key={d.id} className="row" style={{ gap: 8, fontSize: 13 }}>
                    <span style={{ color: d.status === "done" || d.status === "skipped" ? "var(--good)" : "var(--watch)", display: "inline-flex" }}><Icon name={d.status === "done" || d.status === "skipped" ? "check" : "clock"} size={14} /></span>
                    <span style={{ color: d.status === "done" || d.status === "skipped" ? "var(--muted)" : "var(--ink)" }}>{d.label}</span>
                  </div>
                ))}
              </div>
              {unmet.length > 0 && <div className="badge badge--risk" style={{ marginTop: 10 }}><Icon name="lock" size={11} /> Blocked until {unmet.map((d) => d.label).join(", ")} {unmet.length === 1 ? "is" : "are"} done</div>}
            </div>
          )}

          {/* completion fields (from the task's type) */}
          {type && type.fields.length > 0 && state !== "done" && (
            <div>
              <div className="labeled__l" style={{ marginBottom: 8 }}>Complete these to finish the step</div>
              <div style={{ display: "grid", gap: 12 }}>
                {type.fields.map((f) => (
                  f.kind === "checkbox" ? (
                    <label key={f.key} className="row" style={{ gap: 9, fontSize: 14, cursor: "pointer" }}>
                      <input type="checkbox" checked={values[f.key] === true} onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.checked }))} />
                      <span>{f.label}{f.required && <span style={{ color: "var(--risk)" }}> *</span>}</span>
                    </label>
                  ) : (
                    <label key={f.key} style={{ display: "grid", gap: 5 }}>
                      <span className="labeled__l">{f.label}{f.required && <span style={{ color: "var(--risk)" }}> *</span>}{f.help && <span className="faint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}> — {f.help}</span>}</span>
                      {f.kind === "select"
                        ? <select className="inp" value={String(values[f.key] ?? "")} onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}><option value="">Select…</option>{(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}</select>
                        : <input className="inp" type={f.kind === "date" ? "date" : f.kind === "number" ? "number" : "text"} value={values[f.key] === undefined ? "" : String(values[f.key])} onChange={(e) => setValues((s) => ({ ...s, [f.key]: f.kind === "number" ? Number(e.target.value) : e.target.value }))} />}
                    </label>
                  )
                ))}
              </div>
            </div>
          )}

          {state === "done" && <div className="badge badge--good"><Icon name="check" size={12} /> This step is complete.</div>}
        </div>
        <div className="compose-modal__hd" style={{ borderTop: "1px solid var(--line-soft)", borderBottom: "none", justifyContent: "space-between" }}>
          <span className="faint" style={{ fontSize: 12 }}>
            {state === "done" ? "Completed" : unmet.length ? "Blocked by dependencies" : missing.length ? `${missing.length} required field${missing.length === 1 ? "" : "s"} left` : "Ready to complete"}
          </span>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn--sm" onClick={onClose}>Close</button>
            {state !== "done" && <button className="btn btn--primary btn--sm" disabled={!canComplete || busy}
              onClick={async () => { setBusy(true); await onComplete(values); setBusy(false); }}>
              <Icon name="check" size={14} /> Mark complete
            </button>}
          </div>
        </div>
      </div>
    </div>
  );
}
